import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';

// Store MSAL instance for token refresh
let msalInstance: IPublicClientApplication | null = null;
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

// Function to register MSAL instance for token refresh
export const setMsalInstance = (instance: IPublicClientApplication | null) => {
  msalInstance = instance;
};

// Function to refresh token
const refreshToken = async (): Promise<string | null> => {
  if (!msalInstance) {
    console.warn('MSAL instance not available for token refresh');
    return null;
  }

  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.warn('No accounts found for token refresh');
      return null;
    }

    const account = accounts[0];
    console.log('🔄 Attempting to refresh token silently...');
    
    const tokenResponse = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account as AccountInfo
    });

    if (tokenResponse && tokenResponse.accessToken) {
      console.log('✅ Token refreshed successfully');
      setAuthToken(tokenResponse.accessToken);
      return tokenResponse.accessToken;
    }
    
    return null;
  } catch (error: any) {
    console.error('❌ Token refresh failed:', error);
    
    // If silent refresh fails, try interactive (but don't block)
    if (error.errorCode === 'interaction_required' || error.errorCode === 'consent_required') {
      console.log('🔄 Silent refresh requires interaction, attempting popup...');
      try {
        const tokenResponse = await msalInstance.acquireTokenPopup(loginRequest);
        if (tokenResponse && tokenResponse.accessToken) {
          console.log('✅ Token refreshed via popup');
          setAuthToken(tokenResponse.accessToken);
          return tokenResponse.accessToken;
        }
      } catch (popupError) {
        console.error('❌ Popup token refresh also failed:', popupError);
      }
    }
    
    return null;
  }
};

// Process queued requests after token refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

/**
 * API base URL.
 * - Default `/api` in the browser: same origin as the React dev server, so it works with
 *   `package.json` `proxy` → Flask (avoids mixed-content blocks when HTTPS=true on port 3000).
 * - Override with REACT_APP_API_URL if the API is on another host (must match page protocol).
 */
const defaultApiBaseURL =
  typeof window !== 'undefined' && !process.env.REACT_APP_API_URL
    ? `${window.location.origin}/api`
    : process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: defaultApiBaseURL,
  timeout: 30000, // Increased to 30 seconds for large queries
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging and token verification
apiClient.interceptors.request.use(
  (config) => {
    const token = config.headers?.Authorization || apiClient.defaults.headers.common['Authorization'];
    console.log(`Making ${config.method?.toUpperCase()} request to: ${config.url}`);
    if (token && typeof token === 'string') {
      console.log('Authorization header present:', token.substring(0, 20) + '...');
    } else if (!token) {
      console.warn('⚠️ No authorization token found in request!');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and automatic token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    
    // Enhanced error logging
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.error('Request timeout:', originalRequest?.url);
    } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
      console.error('Network error:', originalRequest?.url);
    } else if (error.response) {
      console.error('HTTP error:', error.response.status, originalRequest?.url);
    }

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          isRefreshing = false;
          
          // Retry the original request with new token
          return apiClient(originalRequest);
        } else {
          processQueue(new Error('Token refresh failed'), null);
          isRefreshing = false;
          
          // If refresh failed, redirect to login or show error
          console.error('Token refresh failed, user may need to re-authenticate');
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to create API client with custom timeout
export const createApiClient = (timeout: number = 30000) => {
  const client = axios.create({
    baseURL: defaultApiBaseURL,
    timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });
  
  // Read the token per request so refreshes via setAuthToken() reach clients created earlier
  client.interceptors.request.use((config) => {
    const currentToken = apiClient.defaults.headers.common['Authorization'];
    if (currentToken) {
      config.headers.Authorization = currentToken;
    }
    return config;
  });
  
  // Add the same response interceptor for token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      
      if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return client(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await refreshToken();
          if (newToken && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
            isRefreshing = false;
            return client(originalRequest);
          } else {
            processQueue(new Error('Token refresh failed'), null);
            isRefreshing = false;
            return Promise.reject(error);
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
  
  return client;
};

// Helper function to get user-friendly error messages
export const getErrorMessage = (error: any): string => {
  if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
    return 'Request timed out. Please check your connection and try again.';
  } else if (error.code === 'ERR_NETWORK' || error.message.includes('Network Error')) {
    return 'Network error. Unable to connect to the server. Please check your internet connection.';
  } else if (error.response?.data) {
    const data = error.response.data;
    const details =
      typeof data.details === 'string' && data.details.trim()
        ? data.details.trim()
        : '';
    const base =
      typeof data.error === 'string' && data.error.trim()
        ? data.error.trim()
        : error.response?.status === 500
          ? 'Server error. Please try again later.'
          : error.response?.status === 404
            ? 'API endpoint not found. Please contact support.'
            : error.response?.status === 401
              ? 'Unauthorized. Please check your authentication.'
              : error.response?.status === 403
                ? 'Access forbidden. You do not have permission to access this resource.'
                : '';
    if (base && details) return `${base} (${details})`;
    if (base) return base;
    if (details) return details;
  } else if (error.response?.status === 500) {
    return 'Server error. Please try again later.';
  } else if (error.response?.status === 404) {
    return 'API endpoint not found. Please contact support.';
  } else if (error.response?.status === 401) {
    return 'Unauthorized. Please check your authentication.';
  } else if (error.response?.status === 403) {
    return 'Access forbidden. You do not have permission to access this resource.';
  } else if (error.message) {
    return error.message;
  }

  return 'An unexpected error occurred. Please try again.';
};

// Helper function to set authorization token
export const setAuthToken = (token: string | null) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('✅ Auth token set on API client');
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
    console.log('❌ Auth token removed from API client');
  }
};

export default apiClient;
